import getConfig from 'next/config'
import LocalStorageService from '../../../utils/LocalStorageService'
import {
  setUTMPayload,
  removeUTMPayload,
  validateQueryParamsContainsUTM,
} from './UTMService'

let amplitudeInstance: any = null

async function getAmplitude(userId?: string): Promise<any> {
  if (amplitudeInstance) {
    return amplitudeInstance
  }
  return await initializeAmplitude(userId)
}

async function initializeAmplitude(userId?: string): Promise<any> {
  if (amplitudeInstance) {
    return amplitudeInstance
  }

  try {
    const {
      publicRuntimeConfig: { AMPLITUDE_TOKEN, ENVIRONMENT, AMPLITUDE_SESSION_TIMEOUT },
    } = getConfig()

    const amplitudeModule = await import('amplitude-js')

    amplitudeInstance = amplitudeModule

    amplitudeModule.default.getInstance().init(AMPLITUDE_TOKEN, userId, {
      apiEndpoint:
        ENVIRONMENT === 'PROD'
          ? 'website-api.omni.fairprice.com.sg/api/tracking/events'
          : 'website-api.zs-uat.fairprice.com.sg/api/tracking/events',
        sessionTimeout: AMPLITUDE_SESSION_TIMEOUT ?? 30 * 60 * 1000, // default 30 minutes
    })

    return amplitudeInstance
  } catch (error) {
    console.error('Failed to initialize Amplitude:', error)
    amplitudeInstance = null
    return amplitudeInstance
  }
}

async function getSessionId(userId: string): Promise<string | null> {
  try {
    if (!amplitudeInstance) {
      await initializeAmplitude(userId)
    }

    const amplitudeSessionId = amplitudeInstance.default
      ?.getInstance?.()
      ?.getSessionId?.()

    const hasUTMParams = validateQueryParamsContainsUTM()
    const isSessionIdChanged = validateSessionIdChange()

    LocalStorageService.setItem('analytics_session_id', amplitudeSessionId)

    // Always remove UTM payload on session change
    if (isSessionIdChanged) {
      removeUTMPayload()
      LocalStorageService.setItem(
        'last_analytics_session_id',
        amplitudeSessionId
      )
    }

    // Set UTM payload if UTM parameters exist, regardless of session state
    if (hasUTMParams) {
      setUTMPayload()
    }

    return amplitudeSessionId
  } catch (error) {
    console.error(
      '[AmplitudeService] Error getting Amplitude session ID:',
      error
    )
    return null
  }
}

function resetAmplitudeInstance(): void {
  amplitudeInstance = null
}

function validateSessionIdChange(): boolean {
  const currentSessionId = LocalStorageService.getItem('analytics_session_id')
  const lastSessionId = LocalStorageService.getItem('last_analytics_session_id')
  if (currentSessionId && lastSessionId) {
    return currentSessionId !== lastSessionId
  }
  return false
}

export {
  getAmplitude,
  getSessionId,
  initializeAmplitude,
  resetAmplitudeInstance,
}
